/**
 * Notification sound playback.
 *
 * Browsers reject programmatic audio until the user has interacted with the
 * document, so the very first notification of a session — the one that matters
 * most — is silent unless the element is primed on an earlier user gesture.
 * `prime()` does that once, then `play()` is safe from anywhere.
 */

import { captureException } from "@/config/sentry";

const SOUND_SRC = "/sounds/notification.wav";

/**
 * Minimum gap between two sounds. A burst of pings must read as one alert, not
 * a machine-gun — matches the per-chat coalescing the feed itself uses.
 */
const MIN_INTERVAL_MS = 2000;

class NotificationSound {
  private audio: HTMLAudioElement | null = null;
  private unlocked = false;
  private primeListenersAttached = false;
  private lastPlayedAt = 0;
  private playbackFailureReported = false;

  private getAudio(): HTMLAudioElement | null {
    if (typeof Audio === "undefined") return null;
    if (!this.audio) {
      this.audio = new Audio(SOUND_SRC);
      this.audio.preload = "auto";
    }
    return this.audio;
  }

  /**
   * Attach one-shot listeners that unlock audio on the first user gesture.
   * Safe to call repeatedly; only the first call does anything.
   */
  prime(): void {
    if (this.primeListenersAttached || this.unlocked) return;
    if (typeof window === "undefined") return;

    this.primeListenersAttached = true;

    const unlock = () => {
      const audio = this.getAudio();
      if (!audio) return;

      // Play muted and immediately reset. This marks the element as
      // user-activated, so later programmatic play() calls are allowed.
      const wasMuted = audio.muted;
      audio.muted = true;

      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.muted = wasMuted;
          this.unlocked = true;
        })
        .catch(() => {
          // Still locked — leave the listeners off and fall back to the
          // badge and toast, which need no permission.
          audio.muted = wasMuted;
        })
        .finally(() => {
          detach();
        });
    };

    const detach = () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };

    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
  }

  /**
   * Play the notification sound, subject to the debounce window.
   * Returns true if a sound was actually started.
   */
  play(): boolean {
    const now = Date.now();
    if (now - this.lastPlayedAt < MIN_INTERVAL_MS) return false;

    const audio = this.getAudio();
    if (!audio) return false;

    this.lastPlayedAt = now;

    try {
      audio.currentTime = 0;
    } catch {
      // Safari can throw if metadata hasn't loaded yet; play() still works.
    }

    audio.play().catch((error) => {
      // Almost always NotAllowedError because no gesture has happened yet.
      // Report once so a genuinely broken asset path is still visible.
      if (!this.playbackFailureReported) {
        this.playbackFailureReported = true;
        captureException(error, { scope: "notificationSound.play" });
      }
    });

    return true;
  }

  /** Test hook — lets a caller verify wiring without waiting out the debounce. */
  resetDebounce(): void {
    this.lastPlayedAt = 0;
  }
}

export const notificationSound = new NotificationSound();
